import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  usePatients, useProviders, useLocations, usePayers, usePlans,
  useCreateAppointment, useRunEligibility,
} from '../api/hooks';
import { fullName, fmtDob, money, NOW_DATE } from '../lib/data';
import { CalendarPlus, Plus, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from '../lib/aiToast';
import AddProvider from './AddProvider';
import Pipeline from './Pipeline';
import RiskBadge from './RiskBadge';
import StatusPill from './StatusPill';

// Visit type → representative CPT (visitTypeFor() maps it back for display/risk).
const VISIT_TYPES = [
  { type: 'New patient', cpt: '99203' },
  { type: 'Follow-up', cpt: '99213' },
  { type: 'Procedure', cpt: '11102' },
  { type: 'Surgery', cpt: '17311' },
];
const GENDERS = ['F', 'M', 'Other', 'Unknown'];
// The agent pipeline that runs the moment a visit is booked.
const RUN_STAGES = ['Verification agent · 270 sent', '271 received · benefits parsed', 'Risk agent · scored', 'Routed'];

// Manual "Add appointment". Supports booking for an existing patient OR creating
// a new patient inline (POST /appointments accepts either `patientId` or a
// `patient` object). New appointments land in the worklist as "queued" → Run
// eligibility, matching the core flow.
export default function AddAppointment({ open, onClose, practiceId = 'prac_001' }) {
  const { session } = useAuth();
  const pid = session?.practice?.id || practiceId;

  const { data: patients = [] } = usePatients(pid);
  const { data: providers = [] } = useProviders(pid);
  const { data: locations = [] } = useLocations(pid);
  const { data: payers = [] } = usePayers();
  const { data: plans = [] } = usePlans();
  const create = useCreateAppointment();
  const run = useRunEligibility();
  const nav = useNavigate();

  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  // existing-patient selection
  const [patientSel, setPatientSel] = useState('');
  // new-patient fields
  const [np, setNp] = useState({ firstName: '', lastName: '', dob: '', gender: '', phone: '', payerId: '', planId: '', memberIdLast4: '' });
  // shared appointment fields
  const [providerId, setProviderId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState(NOW_DATE);
  const [tm, setTm] = useState('09:00');
  const [visit, setVisit] = useState('New patient');
  const [reason, setReason] = useState('');
  const [addProviderOpen, setAddProviderOpen] = useState(false);
  // Agent-run phase: after booking, the agents verify eligibility live.
  const [phase, setPhase] = useState('form'); // 'form' | 'running' | 'done'
  const [stage, setStage] = useState(-1);
  const [runResult, setRunResult] = useState(null);
  const [newApptId, setNewApptId] = useState(null);
  const timers = useRef([]);

  useEffect(() => {
    if (open) {
      setMode('existing'); setPatientSel('');
      setNp({ firstName: '', lastName: '', dob: '', gender: '', phone: '', payerId: '', planId: '', memberIdLast4: '' });
      setProviderId(''); setLocationId(''); setDate(NOW_DATE); setTm('09:00'); setVisit('New patient'); setReason('');
      setPhase('form'); setStage(-1); setRunResult(null); setNewApptId(null);
      timers.current.forEach(clearTimeout); timers.current = [];
    }
  }, [open]);

  const setField = (k) => (v) => setNp((s) => ({ ...s, [k]: v, ...(k === 'payerId' ? { planId: '' } : {}) }));
  const plansForPayer = useMemo(
    () => plans.filter((p) => !np.payerId || p.payerId === np.payerId),
    [plans, np.payerId],
  );

  const patientValid = mode === 'existing' ? !!patientSel : (np.firstName.trim() && np.lastName.trim());
  const canSubmit = patientValid && providerId && date && tm && visit && !create.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    const cpt = VISIT_TYPES.find((v) => v.type === visit)?.cpt || '99213';
    const startTime = `${date}T${tm}:00`;
    const base = {
      practiceId: pid, providerId, locationId: locationId || undefined,
      startTime, cpt, reason: reason.trim() || `${visit} visit`,
    };
    const body = mode === 'existing'
      ? { ...base, patientId: patientSel }
      : {
          ...base,
          patient: {
            firstName: np.firstName.trim(), lastName: np.lastName.trim(),
            dob: np.dob || undefined, gender: np.gender || undefined, phone: np.phone || undefined,
            payerId: np.payerId || undefined, planId: np.planId || undefined,
            memberIdLast4: np.memberIdLast4 || undefined, isNewPatient: true,
          },
        };
    const who = mode === 'existing' ? fullName(patients.find((p) => p.id === patientSel)) : `${np.firstName} ${np.lastName}`;

    let appt;
    try {
      appt = await create.mutateAsync(body);
    } catch (e) {
      toast.error(e?.userMessage || 'Could not add appointment');
      return;
    }

    // Booked → the agents verify eligibility automatically. Animate the pipeline.
    setNewApptId(appt.id);
    setPhase('running');
    setStage(0);
    timers.current.forEach(clearTimeout);
    timers.current = RUN_STAGES.map((_, i) => setTimeout(() => setStage(i + 1), 600 * (i + 1)));
    try {
      const res = await run.mutateAsync({ patientId: appt.patientId, appointmentId: appt.id, providerId });
      await new Promise((r) => setTimeout(r, 600 * RUN_STAGES.length + 250)); // let stages finish visually
      setStage(RUN_STAGES.length);
      setRunResult({ who, ...res });
      setPhase('done');
    } catch (e) {
      timers.current.forEach(clearTimeout);
      toast.error('Booked, but the eligibility run failed — run it from the worklist.');
      onClose?.();
    }
  };

  const steps = RUN_STAGES.map((label, i) => ({
    label, status: stage > i ? 'done' : stage === i ? 'active' : 'pending',
  }));

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {phase === 'form'
              ? <><CalendarPlus className="h-4 w-4 text-brand-600" />Add appointment</>
              : <><ShieldCheck className="h-4 w-4 text-brand-600" />{phase === 'done' ? 'Eligibility verified' : 'Agents verifying eligibility…'}</>}
          </DialogTitle>
          <DialogDescription>
            {phase === 'form'
              ? 'Book for an existing patient or add a new one — agents verify eligibility the moment it’s booked.'
              : 'Booking a visit runs the agents automatically: 270/271, benefit parsing, risk scoring, and routing.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'form' ? (
        <>
        <div className="space-y-3.5 py-1">
          {/* Patient mode toggle */}
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-sm">
            {['existing', 'new'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`px-3 py-1.5 rounded-md font-medium transition ${mode === m ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {m === 'existing' ? 'Existing patient' : 'New patient'}
              </button>
            ))}
          </div>

          {mode === 'existing' ? (
            <div className="grid gap-1.5">
              <Label>Patient</Label>
              <Select value={patientSel} onValueChange={setPatientSel}>
                <SelectTrigger><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>
                  {patients.map((p) => <SelectItem key={p.id} value={p.id}>{fullName(p)} · DOB {fmtDob(p.dob)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-3.5 rounded-lg border border-slate-200 p-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label htmlFor="np-fn">First name</Label><Input id="np-fn" value={np.firstName} onChange={(e) => setField('firstName')(e.target.value)} placeholder="Jane" /></div>
                <div className="grid gap-1.5"><Label htmlFor="np-ln">Last name</Label><Input id="np-ln" value={np.lastName} onChange={(e) => setField('lastName')(e.target.value)} placeholder="Doe" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label htmlFor="np-dob">Date of birth</Label><Input id="np-dob" type="date" value={np.dob} onChange={(e) => setField('dob')(e.target.value)} /></div>
                <div className="grid gap-1.5">
                  <Label>Sex <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Select value={np.gender} onValueChange={setField('gender')}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Payer</Label>
                  <Select value={np.payerId} onValueChange={setField('payerId')}>
                    <SelectTrigger><SelectValue placeholder="Select payer" /></SelectTrigger>
                    <SelectContent>{payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Plan <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Select value={np.planId} onValueChange={setField('planId')} disabled={!np.payerId}>
                    <SelectTrigger><SelectValue placeholder={np.payerId ? 'Select plan' : 'Pick payer first'} /></SelectTrigger>
                    <SelectContent>{plansForPayer.map((p) => <SelectItem key={p.id} value={p.id}>{p.planName}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label htmlFor="np-mem">Member ID (last 4)</Label><Input id="np-mem" value={np.memberIdLast4} maxLength={4} onChange={(e) => setField('memberIdLast4')(e.target.value)} placeholder="4521" /></div>
                <div className="grid gap-1.5"><Label htmlFor="np-phone">Phone <span className="text-slate-400 font-normal">(optional)</span></Label><Input id="np-phone" value={np.phone} onChange={(e) => setField('phone')(e.target.value)} placeholder="(415) 555-0100" /></div>
              </div>
            </div>
          )}

          <div className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label>Provider</Label>
              <button type="button" onClick={() => setAddProviderOpen(true)} className="text-xs font-medium text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
                <Plus className="h-3 w-3" />New provider
              </button>
            </div>
            <Select key={`prov-${providers.length}`} value={providerId} onValueChange={setProviderId}>
              <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
              <SelectContent>{providers.map((p) => <SelectItem key={p.id} value={p.id}>Dr. {p.firstName} {p.lastName}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {locations.length > 0 && (
            <div className="grid gap-1.5">
              <Label>Department <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5"><Label htmlFor="ap-date">Date</Label><Input id="ap-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div className="grid gap-1.5"><Label htmlFor="ap-time">Time</Label><Input id="ap-time" type="time" value={tm} onChange={(e) => setTm(e.target.value)} /></div>
            <div className="grid gap-1.5">
              <Label>Visit type</Label>
              <Select value={visit} onValueChange={setVisit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{VISIT_TYPES.map((v) => <SelectItem key={v.type} value={v.type}>{v.type}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="ap-reason">Reason <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input id="ap-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Annual skin exam" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose?.()}>Cancel</Button>
          <Button className="bg-brand-600 hover:bg-brand-700 text-white" disabled={!canSubmit} onClick={submit}>Add appointment &amp; verify</Button>
        </DialogFooter>
        </>
        ) : (
        <>
        <div className="space-y-4 py-2">
          <div className="dh-inset p-4"><Pipeline steps={steps} /></div>

          {phase === 'running' && (
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-brand-600" />Agents are verifying eligibility…
            </div>
          )}

          {phase === 'done' && runResult?.check && (
            <div className="ai-surface p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-slate-500">Plan returned</div>
                  <div className="font-semibold text-slate-900 truncate">{runResult.check.planNameReturned}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusPill status={runResult.network?.status || (runResult.check.coverage?.active === false ? 'inactive' : 'active')} />
                  <RiskBadge level={runResult.risk?.level} score={runResult.risk?.score} showScore />
                </div>
              </div>
              <div className="text-sm text-slate-700 mt-2">{runResult.check.summaryText}</div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div className="dh-inset p-2.5"><div className="text-slate-500">Copay</div><div className="font-semibold tnum mt-0.5">{money(runResult.check.financials?.copay)}</div></div>
                <div className="dh-inset p-2.5"><div className="text-slate-500">Deductible</div><div className="font-semibold tnum mt-0.5">{money(runResult.check.financials?.deductibleIndMet)}/{money(runResult.check.financials?.deductibleInd)}</div></div>
                <div className="dh-inset p-2.5"><div className="text-slate-500">Outcome</div><div className="font-semibold mt-0.5">{['clear', 'low'].includes(runResult.risk?.level) ? 'Auto-cleared' : 'Routed to you'}</div></div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {phase === 'done' ? (
            <>
              <Button variant="ghost" onClick={() => onClose?.()}>Close</Button>
              <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => { onClose?.(); nav(`/app/appointments/${newApptId}`); }}>View appointment</Button>
            </>
          ) : (
            <Button variant="ghost" disabled><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying…</Button>
          )}
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>

    <AddProvider
      open={addProviderOpen}
      onClose={() => setAddProviderOpen(false)}
      practiceId={pid}
      onCreated={(p) => setProviderId(p.id)}
    />
    </>
  );
}
